import React, { Component } from 'react'
import { Button, Icon, Pagination, Popconfirm, Table, Tag, Tooltip } from 'antd'
import { formatAmount } from '../../../utils'
import {
    CLIENT_CELL_TEXT,
    ISSUE_COLOR,
    RESOLUTION_MODE_COLOR,
    RESOLUTION_MODE_TEXT_COLOR,
    USER_STATE_COLOR,
    autoMatchConfirmText,
    autoSkuConfirmText,
    canAcceptAutoMatch,
    canAcceptAutoSku,
    canAssignManualUser,
    canAssignManualSku,
    clientPagePath,
    instrumentStateText,
    issueText,
    resolutionModeText,
    skuCandidateText,
    skuStateText
} from '../utils'

// The SAME table as Documents/List, with this queue's own columns. Everything visual is taken
// from there rather than invented: the `.box` wrapper, `center-table`, size="middle", bordered,
// the zebra rowClassName, the computed scroll.x, the pagination block and the two-line cell
// idiom (a normal line plus a 0.75rem muted line). The two tabs have to read as two tables on
// one page, so Documents stays the source of truth and this one follows it.
//
// Three actions, in the אופן הטיפול cell: "אשר שיוך" on rows the server marks
// auto_resolve_available -- AUTO_AVAILABLE, and MANUAL_USER_AND_SKU_REQUIRED with the same evidence
// (behind a confirmation whose candidate is sent as expected_user_id), "שייך לקוח" on MANUAL_USER_REQUIRED and MANUAL_USER_AND_SKU_REQUIRED rows (opens
// the queue's candidate modal; assigns the user only) and
// "אשר מק״ט" on MANUAL_SKU_REQUIRED rows with a server-offered local code (behind a confirmation).
// Plus "שייך מק״ט" on rows the server marks manual_sku_available (opens the manual SKU modal).
// Every other mode stays read-only -- no global client search.
//
// PRIVACY: this table renders what the API chose to say -- states, booleans, enums, and for a
// VALID row the CURRENT client's display name (user_name) and id. It never receives an identity
// number, an email, a phone, a card token, a bank tuple or a raw UDF, so it cannot render one.

// The two presentation primitives the Documents table uses, reproduced here verbatim rather
// than imported: extracting them would mean editing Documents/List, and that file is the
// approved visual reference this table is being aligned to. antd 3.23 has no column.ellipsis
// (it arrived in 3.25), so long text is clipped with a Tooltip wrapper -- without it a long
// value stretches its column and breaks the width sum scroll.x depends on.
const DASH = '—'

const clip = (title, children = null, style = {}) => (
    <Tooltip title={title || ''}>
        <span
            style={Object.assign(
                {
                    display: 'block',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                },
                style
            )}
        >
            {children || title || DASH}
        </span>
    </Tooltip>
)

// Same muted secondary line the Documents table uses under a primary value.
const SECOND_LINE = { display: 'block', fontSize: '0.75rem', color: '#8c8c8c' }
const TWO_LINE = { display: 'block', lineHeight: '1.25rem' }

const muted = text => <span style={{ color: '#bfbfbf' }}>{text}</span>

export default class UnresolvedList extends Component<any, any> {
    // Every problem the row has, never only the first: a dangling reference that is also missing
    // its sku is two jobs and must read as two. Inline rather than stacked, so a two-issue row
    // stays the same height as every other row in the table.
    issuesCell = row => (
        <span style={{ display: 'block' }}>
            {(row.issues || []).map(issue => (
                <Tag key={issue} color={ISSUE_COLOR[issue]} style={{ marginLeft: 4, marginRight: 0 }}>
                    {issueText(issue)}
                </Tag>
            ))}
        </span>
    )

    // The CURRENT client only. user_id + user_name describe who the row belongs to now;
    // auto_resolve_user_id is a PROPOSED candidate and is deliberately never read here.
    //   VALID    -> the client's name and id, linking to the existing client page
    //   MISSING  -> לא משויך, no link
    //   DANGLING -> לקוח לא קיים plus the stored id, no link: there is no page to open
    userCell = row => {
        if (row.user_state === 'VALID' && row.user_id) {
            const name = row.user_name || ''
            const link = (
                <a href={clientPagePath(row.user_id)} target="_blank" rel="noopener noreferrer" style={TWO_LINE}>
                    {name ? <span>{name}</span> : null}
                    <span style={name ? SECOND_LINE : undefined} dir="ltr">
                        {row.user_id}
                    </span>
                </a>
            )
            return clip(name ? `${name} (${row.user_id})` : String(row.user_id), link)
        }

        const dangling = row.user_state === 'DANGLING'
        const label = dangling ? CLIENT_CELL_TEXT.DANGLING : CLIENT_CELL_TEXT.MISSING
        const body = (
            <span style={TWO_LINE}>
                <Tag color={USER_STATE_COLOR[row.user_state]} style={{ margin: 0 }}>
                    {label}
                </Tag>
                {/* The STORED reference, so an operator can see an attribution was made and now
                    resolves to nobody. Plain text: a dangling id has no client page. */}
                {dangling && row.user_id ? (
                    <span style={SECOND_LINE} dir="ltr">
                        {row.user_id}
                    </span>
                ) : null}
            </span>
        )
        return clip(dangling && row.user_id ? `${label} (${row.user_id})` : label, body)
    }

    skuCell = row => {
        const body = (
            <span style={TWO_LINE}>
                <span dir="ltr">{row.sku || muted(DASH)}</span>
                <span style={SECOND_LINE}>{skuStateText(row.sku_state)}</span>
            </span>
        )
        return clip(`${row.sku || DASH} — ${skuStateText(row.sku_state)}`, body)
    }

    // Safe evidence only: "is there a display name", "what does the payment instrument say",
    // "what does the local product text look like", "does this terminal serve items at all".
    // Two lines in the table, the whole picture in the tooltip.
    evidenceCell = row => {
        const evidence = row.evidence || {}
        const full = [
            `שם לתצוגה: ${evidence.udf2_present ? 'קיים' : 'חסר'}`,
            `אמצעי תשלום: ${instrumentStateText(evidence.instrument_match_state)}`,
            `מקור מק"ט מקומי: ${skuCandidateText(evidence.sku_candidate_state)}`,
            `פריטים במסוף: ${evidence.upstream_items_eligible ? 'נתמך' : 'לא נתמך'}`
        ].join(' · ')

        const body = (
            <span style={TWO_LINE}>
                <span>{instrumentStateText(evidence.instrument_match_state)}</span>
                <span style={SECOND_LINE}>{skuCandidateText(evidence.sku_candidate_state)}</span>
            </span>
        )
        return clip(full, body)
    }

    // Shaped like the Documents status column: one full-width Tag with centred, clipped text.
    statusCell = row => {
        // Darker text of the preset's own hue: see RESOLUTION_MODE_TEXT_COLOR.
        const color =
            RESOLUTION_MODE_TEXT_COLOR[row.resolution_mode] || RESOLUTION_MODE_TEXT_COLOR.INVESTIGATION_REQUIRED
        return (
            <span style={TWO_LINE}>
                <Tag
                    color={RESOLUTION_MODE_COLOR[row.resolution_mode] || 'purple'}
                    style={{ display: 'block', width: '100%', maxWidth: '100%', margin: 0, overflow: 'hidden', color }}
                >
                    {clip(resolutionModeText(row.resolution_mode), null, { textAlign: 'center' })}
                </Tag>
                {/* Informational only in Phase 1 -- it reports that a deterministic candidate EXISTS,
                    and nothing here can act on it. */}
                {row.auto_resolve_available ? (
                    <span style={SECOND_LINE} dir="ltr">
                        {row.auto_resolve_user_id}
                    </span>
                ) : null}
                {canAcceptAutoMatch(row) ? this.autoMatchAction(row) : null}
                {canAssignManualUser(row) ? this.manualAssignAction(row) : null}
                {canAcceptAutoSku(row) ? this.autoSkuAction(row) : null}
                {canAssignManualSku(row) ? this.manualSkuAction(row) : null}
            </span>
        )
    }

    // While ANY acceptance is in flight every button is disabled, so a double click -- or a second
    // row -- cannot submit again before the first answer arrives.
    autoMatchAction = row => {
        const { onResolveUserAuto, resolvingId } = this.props
        if (typeof onResolveUserAuto !== 'function') return null
        const busy = resolvingId !== null && resolvingId !== undefined
        return (
            <Popconfirm
                title={autoMatchConfirmText(row)}
                okText="אשר"
                cancelText="ביטול"
                disabled={busy}
                onConfirm={() => onResolveUserAuto(row)}
            >
                <Button
                    size="small"
                    type="primary"
                    disabled={busy}
                    loading={busy && resolvingId === row.id}
                    style={{ marginTop: 4 }}
                >
                    אשר שיוך
                </Button>
            </Popconfirm>
        )
    }

    // Same shape as autoMatchAction: a confirmation, disabled while any fix is in flight.
    autoSkuAction = row => {
        const { onResolveSkuAuto, resolvingId } = this.props
        if (typeof onResolveSkuAuto !== 'function') return null
        const busy = resolvingId !== null && resolvingId !== undefined
        return (
            <Popconfirm
                title={autoSkuConfirmText(row)}
                okText="אשר"
                cancelText="ביטול"
                disabled={busy}
                onConfirm={() => onResolveSkuAuto(row)}
            >
                <Button
                    size="small"
                    type="primary"
                    disabled={busy}
                    loading={busy && resolvingId === row.id}
                    style={{ marginTop: 4 }}
                >
                    אשר מק״ט
                </Button>
            </Popconfirm>
        )
    }

    // Opens the modal; the modal's own confirm is the write. Disabled while any fix is in flight.
    manualAssignAction = row => {
        const { onAssignUser, resolvingId } = this.props
        if (typeof onAssignUser !== 'function') return null
        const busy = resolvingId !== null && resolvingId !== undefined
        return (
            <Button
                size="small"
                type="primary"
                disabled={busy}
                loading={busy && resolvingId === row.id}
                style={{ marginTop: 4 }}
                onClick={() => onAssignUser(row)}
            >
                שייך לקוח
            </Button>
        )
    }

    // Manual SKU V1: opens the modal; its second confirmation is the write. Disabled while any fix
    // is in flight. canAssignManualSku excludes every row "אשר מק״ט" is drawn for.
    manualSkuAction = row => {
        const { onAssignSku, resolvingId } = this.props
        if (typeof onAssignSku !== 'function') return null
        const busy = resolvingId !== null && resolvingId !== undefined
        return (
            <Button
                size="small"
                type="primary"
                disabled={busy}
                loading={busy && resolvingId === row.id}
                style={{ marginTop: 4 }}
                onClick={() => onAssignSku(row)}
            >
                שייך מק״ט
            </Button>
        )
    }

    // Mirrors Documents/List: a spinner while this row's detail loads, the info-circle otherwise.
    // The queue response stays privacy-minimized -- the detail is fetched by the PAGE, per click.
    transactionDetailsTrigger = row => {
        const { onOpenTransactionDetails, detailsLoadingId } = this.props
        if (!onOpenTransactionDetails) return null
        if (detailsLoadingId !== null && detailsLoadingId !== undefined && detailsLoadingId === row.id) {
            return <Icon type="loading" style={{ color: '#e77910', marginRight: 4 }} />
        }
        return (
            <Icon
                type="info-circle"
                style={{ color: '#e77910', cursor: 'pointer', marginRight: 4 }}
                onClick={() => onOpenTransactionDetails(row.id)}
            />
        )
    }

    // One array, same shape as Documents: title / key / width / align / render.
    getColumns = () => [
        {
            title: 'תאריך',
            key: 'transaction_date',
            width: 120,
            align: 'right',
            render: (_, row) => (
                <span style={TWO_LINE}>
                    <span>{row.transaction_date || DASH}</span>
                    <span style={SECOND_LINE}>{row.transaction_time || ''}</span>
                </span>
            )
        },
        {
            title: 'מס׳ עסקה',
            key: 'transaction_index',
            width: 110,
            align: 'right',
            // A decimal string from the API, rendered as one: never Number()'d anywhere on this
            // path, so a BIGINT index cannot lose its last digits.
            // The SAME trigger as the Documents table and the Transactions page. A VIEW action:
            // it reads one row's detail on demand and changes nothing.
            render: (_, row) => (
                <span>
                    <span dir="ltr">{row.transaction_index}</span> {this.transactionDetailsTrigger(row)}
                </span>
            )
        },
        {
            title: 'מסוף',
            key: 'terminal_name',
            width: 130,
            align: 'right',
            render: (_, row) => <span dir="ltr">{row.terminal_name || DASH}</span>
        },
        {
            title: 'סכום',
            key: 'amount',
            width: 100,
            align: 'right',
            // agorot in the cache; the SAME agorot -> ₪ helper the Documents table uses.
            render: (_, row) => <span dir="ltr">{formatAmount(row.amount) || DASH}</span>
        },
        { title: 'בעיה', key: 'issues', width: 190, align: 'right', render: (_, row) => this.issuesCell(row) },
        { title: 'לקוח', key: 'user', width: 130, align: 'right', render: (_, row) => this.userCell(row) },
        { title: 'מק"ט', key: 'sku', width: 120, align: 'right', render: (_, row) => this.skuCell(row) },
        { title: 'ממצאים', key: 'evidence', width: 180, align: 'right', render: (_, row) => this.evidenceCell(row) },
        { title: 'אופן הטיפול', key: 'status', width: 190, align: 'right', render: (_, row) => this.statusCell(row) }
    ]

    render() {
        const { rows, loading, page, perPage, total, onPageChange } = this.props
        const columns = this.getColumns()
        // Without scroll.x the table is cut off by the viewport instead of scrolling inside its
        // own wrapper. Summed from the columns, exactly as Documents does it.
        const totalWidth = columns.reduce((sum, column) => sum + column.width, 0)

        return (
            <div style={{ minWidth: 0 }}>
                <div className="box">
                    <Table
                        className="center-table"
                        style={{ width: '100%' }}
                        scroll={{ x: totalWidth }}
                        rowKey="id"
                        size="middle"
                        bordered
                        columns={columns}
                        dataSource={rows}
                        loading={loading}
                        rowClassName={(_, index) => (index % 2 ? 'row-odd' : 'row-even')}
                        locale={{ emptyText: 'אין רשומות הדורשות טיפול' }}
                        pagination={false}
                    />
                </div>
                <div style={{ textAlign: 'left', padding: '12px 16px' }}>
                    <Pagination
                        current={page}
                        pageSize={perPage}
                        total={total}
                        showSizeChanger={false}
                        showTotal={totalRows => `סה״כ ${totalRows} רשומות`}
                        onChange={onPageChange}
                    />
                </div>
            </div>
        )
    }
}
